import { bodyCopyMedium, Crumblist, ISelectOption, ModalPopup, Pullout, PulloutType, Select } from '@aon/aon-tws-common-ui';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import * as React from 'react';
import _ from 'lodash';
import ParseHtml from 'html-react-parser';
import { Provider, useStore } from 'react-redux';
import styled from 'styled-components';
import { ReadonlyHierarchy } from '../../containers/hierarchy/ReadonlyHierarchy';
import ContentConstants from '../../contentConstants';
import { GroupType, IHasValidationModel, IProfile } from '../../models';
import { TFetchableEntity } from '../../models/IFetchableEntity';
import IHierarchyGroupAccess from '../../models/IHierarchyGroupAccess';
import { IHierarchyReportingAccessConfig } from '../../models/IHierarchyReportingAccessConfig';
import { SurveyHelper, ValidationCollectionHelper } from '../../utilities';
import { getGroupTypeFlagsAsArray } from '../../utilities/GroupTypeEnumHelper';
import { ConditionContainer, ConditionText, CrumblistContainer } from '../common';

const ModalContainer = styled.div`
	width: 100%;
	${bodyCopyMedium}
	font-weight: 600 !important;
	color: ${globalVars.gray1} !important;
`;

export interface IHierarchyReportingAccessConfigModalProps extends IHasValidationModel {
	hierarchyReportingAccessConfigs: IHierarchyReportingAccessConfig[];
	hierarchies: TFetchableEntity<IHierarchyGroupAccess[]>;
	initialModel?: IHierarchyReportingAccessConfig;
	isOpen: boolean;
	editPersonSelectionEnabled: boolean;
	profiles: TFetchableEntity<IProfile[]>;
	saving: boolean;
	onClosed: () => void;
	onUpdate: (model: IHierarchyReportingAccessConfig, closeModal: boolean) => void;
	onAdd: (model: IHierarchyReportingAccessConfig) => void;
}

interface IHierarchyReportingAccessConfigState {
	hierarchyId?: string;
	couldNotFetchHierarchyData?: true;
	profileGroupAccessIds: string[];
	scopeGroupTypes: string[];
	comparisonGroupTypes: string[];
	mode: 'Add' | 'Edit';
}

export const HierarchyReportingAccessConfigModalStoreWrapper = (props: IHierarchyReportingAccessConfigModalProps) => {
	const store = useStore();
	return <HierarchyReportingAccessConfigModal {...props} store={store} />;
};

export class HierarchyReportingAccessConfigModal extends React.Component<
	IHierarchyReportingAccessConfigModalProps & { store: any },
	IHierarchyReportingAccessConfigState
> {
	public constructor(props: IHierarchyReportingAccessConfigModalProps & { store: any }) {
		super(props);
		this.state = this.getDefaultState();
		this.closeModal = this.closeModal.bind(this);
		this.saveModal = this.saveModal.bind(this);
		this.saveAndClose = this.saveAndClose.bind(this);
	}

	private get Warnings() {
		return {
			...(() => {
				const { hierarchies, profiles } = this.props;
				const groupAccessInfo = [hierarchies, profiles];

				if (groupAccessInfo.some((info) => !info.isLoading && info.couldNotFetchData) || this.state.couldNotFetchHierarchyData) {
					return {
						'Info N/A': ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING,
					};
				}
			})(),
		};
	}

	private get selectableHierarchies(): ISelectOption[] {
		const { hierarchies } = this.props;

		if (hierarchies.isLoading || hierarchies.couldNotFetchData) {
			return [];
		}

		return hierarchies
			.filter((h) => this.ProfilesAvailableFor(h.hierarchyId).length > 0)
			.map((h) => ({
				name: h.hierarchyId,
				displayText: h.name,
			}));
	}

	private get selectableProfiles(): ISelectOption[] {
		const { hierarchyId } = this.state;

		if (!hierarchyId) {
			return [];
		}

		return this.ProfilesAvailableFor(hierarchyId).map((p) => ({
			name: p.id,
			displayText: p.name,
		}));
	}

	private get scopeSelectOptions(): ISelectOption[] {
		return [
			{
				name: GroupType.TheirGroupOnly.toString(),
				displayText: ContentConstants.GROUPTYPE_THEIRGROUPONLY,
			},
			{
				name: GroupType.TheirGroupAndAllBelow.toString(),
				displayText: ContentConstants.GROUPTYPE_THEIRGROUPANDALLBELOW,
			},
			{
				name: GroupType.TheirRollupsOnly.toString(),
				displayText: ContentConstants.GROUPTYPE_THEIRROLLUPSONLY,
			},
			{
				name: GroupType.TheirRollupsAndAllBelow.toString(),
				displayText: ContentConstants.GROUPTYPE_THEIRROLLUPSANDALLBELOW,
			},
		];
	}

	public componentDidUpdate(oldProps: IHierarchyReportingAccessConfigModalProps) {
		const getGroupTypeStringValues = (enumValue: GroupType) => getGroupTypeFlagsAsArray(enumValue).map((g) => g + '');

		const { initialModel } = this.props;
		if (initialModel !== oldProps.initialModel) {
			if (initialModel && initialModel.hierarchyReportingAccessConfigId) {
				this.setState({
					hierarchyId: initialModel.hierarchyId,
					profileGroupAccessIds: initialModel.groupAccessProfiles.map((p) => p.profileId),
					scopeGroupTypes: getGroupTypeStringValues(initialModel.scopeGroupTypes),
					comparisonGroupTypes: getGroupTypeStringValues(initialModel.comparisonGroupTypes),
					mode: 'Edit',
				});
			} else {
				this.setState(this.getDefaultState());
			}
		}
	}

	public render() {
		const saveDisabled = this.props.saving || (this.props.initialModel && _.isEqual(this.props.initialModel, this.getModelFromState()));

		const modeProps =
			this.state.mode === 'Add'
				? {
						title: ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_ADD_TITLE,
						primaryButtonText: ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_ADD_BUTTON,
						primaryButtonDisabled: saveDisabled,
						onSubmit: this.saveAndClose,
				  }
				: {
						title: ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_EDIT_TITLE,
						alternatePrimaryButtonText: ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_SAVE_AND_CLOSE_BUTTON,
						primaryButtonText: ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_SAVE_BUTTON,
						primaryButtonDisabled: saveDisabled,
						alternatePrimaryButtonDisabled: saveDisabled,
						onAlternatePrimaryButtonClick: this.saveAndClose,
						onSubmit: this.saveModal,
				  };

		const validationHelper = new ValidationCollectionHelper(this.props.validationResults);

		const personSelectionEnabled = this.state.mode !== 'Edit' || this.props.editPersonSelectionEnabled;

		return (
			<ModalPopup
				{...modeProps}
				isOpen={this.props.isOpen}
				onClosed={this.closeModal}
				contentfullwidth={true}
				contenthcentered={false}
				contentoverflow={true}
				secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
			>
				<Provider store={this.props.store}>
					<ModalContainer>
						{!personSelectionEnabled && (
							<>
								<Pullout type={PulloutType.Locked}>{ParseHtml(ContentConstants.SURVEY_ADMIN_REPORTING_USERS_LOCKED_MESSAGE)}</Pullout>
								<br />
							</>
						)}
						{Object.entries(this.Warnings).map(([key, warning]) => (
							<div key={key}>
								<Pullout type={PulloutType.Warning}>{ParseHtml(warning)}</Pullout>
								<br />
							</div>
						))}

						<ConditionContainer>
							<div className='row'>
								<ConditionText className='col-md-auto'>
									{ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_HIERARCHY_SELECT_LABEL}
								</ConditionText>
								<div className='col'>
									<Select
										error={!validationHelper.IsValid('HierarchyId')}
										message={validationHelper.Errors('HierarchyId').toString()}
										selectedValue={this.state.hierarchyId}
										size='md'
										displayText={ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_HIERARCHY_SELECT_PLACEHOLDER}
										onApply={(value) => this.selectHierarchy(value as string)}
										disabled={!personSelectionEnabled || this.selectableHierarchies.length === 0}
										items={this.selectableHierarchies}
									/>
								</div>
							</div>
							<br />
							<div className='row'>
								<ConditionText className='col-md-auto'>
									{ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_PROFILE_SELECT_LABEL}
								</ConditionText>
								<div className='col'>
									<Select
										error={!validationHelper.IsValid('GroupAccessProfiles')}
										message={validationHelper.Errors('GroupAccessProfiles').toString()}
										size='md'
										multiple={true}
										showCount={true}
										displayText={ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_PROFILE_SELECT_PLACEHOLDER}
										items={this.selectableProfiles}
										selectedValues={this.state.profileGroupAccessIds}
										disabled={!personSelectionEnabled || this.selectableProfiles.length === 0}
										onApply={(values) => {
											this.setState({ profileGroupAccessIds: values as string[] });
										}}
									/>
									<CrumblistContainer>
										<Crumblist
											enableDismiss={true}
											data={SurveyHelper.getCrumbsFromSelectOptions(this.selectableProfiles, this.state.profileGroupAccessIds).map((c) => ({
												...c,
												isDismissible: this.props.editPersonSelectionEnabled,
											}))}
											update={(cl) => this.setState({ profileGroupAccessIds: cl.map((c) => c.value) })}
										/>
									</CrumblistContainer>
								</div>
							</div>
						</ConditionContainer>
						{this.state.hierarchyId && (
							<ConditionContainer>
								<ReadonlyHierarchy
									hierarchyId={this.state.hierarchyId}
									includeOwners={true}
									hideCheckboxes={true}
									selectionCriteria={undefined}
									whenDataNotAvailable={{ handle: () => this.setState({ couldNotFetchHierarchyData: true }) }}
								/>
							</ConditionContainer>
						)}
						<br />
						<ConditionContainer>
							<div className='row'>
								<ConditionText className='col-md-auto'>
									{ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_SCOPE_SELECT_LABEL}
								</ConditionText>
								<div className='col'>
									<Select
										error={!validationHelper.IsValid('ScopeGroupTypes')}
										message={validationHelper.Errors('ScopeGroupTypes').toString()}
										size='md'
										multiple={true}
										showCount={true}
										displayText={ContentConstants.HIERARCHY_REPORTING_ACCESS_CONFIG_MODAL_SCOPE_SELECT_PLACEHOLDER}
										items={this.scopeSelectOptions}
										selectedValues={this.state.scopeGroupTypes}
										onApply={(values) => {
											this.setState({ scopeGroupTypes: values as string[] });
										}}
									/>
								</div>
							</div>
						</ConditionContainer>
						{/* <br />
						<ConditionContainer>
							<div className='row'>
								<ConditionText className='col-md-auto'>
									and be able to compare these results to
								</ConditionText>
								<div className='col'>
									<Select
										size='md'
										displayText={'Select comparisons'}
										multiple={true}
									/>
								</div>
							</div>
						</ConditionContainer> */}
					</ModalContainer>
				</Provider>
			</ModalPopup>
		);
	}

	private getDefaultState() {
		// Automatically fill all profiles as selected.
		// When more profiles are available, then consider a default subselection.
		const config: IHierarchyReportingAccessConfigState = {
			hierarchyId: undefined,
			profileGroupAccessIds: [],
			scopeGroupTypes: [],
			comparisonGroupTypes: [],
			mode: 'Add',
		};

		return config;
	}

	private selectHierarchy(hierarchyId: string) {
		this.setState({
			hierarchyId,
			// setting all profiles to be selected by default
			profileGroupAccessIds: this.ProfilesAvailableFor(hierarchyId).map((p) => p.id),
		});
	}

	private closeModal() {
		this.setState(this.getDefaultState());
		this.props.onClosed();
	}

	private getModelFromState() {
		const { initialModel } = this.props;
		const { hierarchyId, profileGroupAccessIds, scopeGroupTypes, comparisonGroupTypes } = this.state;

		const hierarchies = this.props.hierarchies instanceof Array ? this.props.hierarchies : [];
		const profiles = new Map(this.props.profiles instanceof Array ? this.props.profiles.map((p) => [p.id, p]) : []);

		const model: IHierarchyReportingAccessConfig = {
			...(initialModel || { userCount: 0 }),

			hierarchyId,
			hierarchyName: hierarchies.find((h) => h.hierarchyId === hierarchyId)?.name ?? '',
			groupAccessProfiles: profileGroupAccessIds.map((id) => ({ profileId: id, name: profiles.get(id)?.name ?? '' })),
			scopeGroupTypes: _.sum(scopeGroupTypes.map((str) => parseInt(str, 10))),
			comparisonGroupTypes: _.sum(comparisonGroupTypes.map((str) => parseInt(str, 10))),
		};
		return model;
	}

	private async saveModal() {
		const model = this.getModelFromState();
		this.props.onUpdate(model, false);
	}

	private async saveAndClose() {
		const model = this.getModelFromState();

		if (this.state.mode === 'Add') {
			this.props.onAdd(model);
		} else {
			this.props.onUpdate(model, true);
		}
	}

	private ProfilesAvailableFor(hierarchyId: string) {
		const { initialModel, hierarchyReportingAccessConfigs, profiles } = this.props;

		if (profiles.isLoading || profiles.couldNotFetchData) {
			return [];
		}

		// Determine the profiles with GA users bound to the hierarchy
		const profilesForHierarchy = profiles.filter((p) => p.hierarchies.includes(hierarchyId));

		const selectedProfiles = hierarchyReportingAccessConfigs
			// exclude selected properties from the currently selected config
			.filter((h) => !initialModel || initialModel.hierarchyReportingAccessConfigId !== h.hierarchyReportingAccessConfigId)
			// get hierarchies that share a hierarchy with the one selected
			.filter((h) => h.hierarchyId === hierarchyId)
			// get the profileIds by hierarchy
			.map((h) => h.groupAccessProfiles.map((p) => p.profileId));

		// put the profiles ids in one array
		const usedProfiles = _.flatten(selectedProfiles);

		// return profiles not used by any config with the hierarchyId
		return profilesForHierarchy.filter((p) => usedProfiles.indexOf(p.id) === -1);
	}
}
